import React, { createContext, useContext, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchWhiteLabelCompany } from "../reducers/whitelabelSlices";

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

// Theme definitions
const defaultTheme = {
  primary: "#005068",
  secondary: "#f4f9ff",
  text_primary: "#0D0D0D",
  text_secondary: "#B3B3B3",
  base_background: "#ffffff",
  background: "#F8F9FE",
  Sidebar_highlight: "#f8f9fe",
  sideBarText: "#ffffff",
};

const lightTheme = {
  primary: "#2B6CB0", // Medium blue (main brand color)
  secondary: "#E3F2FD", // Soft sky blue for accents/backgrounds
  text_primary: "#1A202C", // Charcoal/slate for primary text
  text_secondary: "#4A5568", // Muted gray-blue for descriptions
  base_background: "#F7FAFC", // Very light bluish-gray for base layout
  background: "#FFFFFF", // Pure white for main areas
  Sidebar_highlight: "#D0E7FB", // Lighter blue for sidebar active item
  sideBarText: "#2B6CB0", // Matches brand blue for sidebar text
  core_background: "#ffffff", // White card/component background
};

const darkTheme = {
  primary: "#72599C",
  secondary: "#0D0D0D",
  text_primary: "#FFFFFF",
  text_secondary: "#FFFFFF",
  base_background: "#131313",
  background: "#131313",
  Sidebar_highlight: "#0D0D0D",
  sideBarText: "#ffffff",
};

const navyTheme = {
  primary: "#002B5B", // Deep Navy Blue
  secondary: "#E6F0FA", // Very light navy-tinted background
  text_primary: "#0D1B2A", // Dark navy text (near-black)
  text_secondary: "#5B738A", // Muted gray-blue for secondary text
  base_background: "#DCEAF6", // Soft blue background layer
  background: "#F4F9FD", // Main page background
  Sidebar_highlight: "#BFD8EF", // Lighter highlight for sidebar
  sideBarText: "#ffffff", // White sidebar text
  core_background: "#ffffff", // Card/section background
};
const greenTheme = {
  primary: "#006B4F",         // Deep green for buttons, accents, or branding
  secondary: "#F4FCF7",       // Very light green-tinted background
  text_primary: "#1A1A1A",    // Deep gray for high readability
  text_secondary: "#374151",  // Cool gray for muted or secondary content
  base_background: "#D9F3E5", // Soft green background for layout sections
  background: "#F4FCF7",      // Page-level background, very light and clean
  Sidebar_highlight: "#CFE8D9", // Slightly stronger green for active sidebar item
  sideBarText: "#ffffff",     // White for contrast against primary or highlight
  core_background: "#ffffff", // White card/modal/container background
};


const orangeTheme = {
  primary: "#B45309", // Burnt orange for buttons, headers, highlights
  secondary: "#FFFBF7", // Off-white with a warm tint for soft backgrounds
  text_primary: "#1F1F1F", // Deep neutral for main text (high readability)
  text_secondary: "#4B5563", // Muted gray for subtext and metadata
  base_background: "#FFE8D1", // Pale orange for layout sections/panels
  background: "#fff9f3", // Slightly off-white main page background
  Sidebar_highlight: "#E6AA5C", // Lighter orange for active sidebar items
  sideBarText: "#ffffff", // White text for dark sidebar backgrounds
  core_background: "#ffffff", // Pure white for cards, modals, containers
};

const themeMap = {
  default: defaultTheme,
  light: lightTheme,
  dark: darkTheme,
  navy: navyTheme,
  green: greenTheme,
  orange: orangeTheme,
};

export function ThemeProvider({ children }) {
  const dispatch = useDispatch();

  const getInitialTheme = () => {
    const storedTheme = localStorage.getItem("themeName");
    return themeMap[storedTheme?.toLowerCase()] || defaultTheme;
  };

  const [theme, setThemeState] = useState(getInitialTheme);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchTheme = async () => {
      if (!token) return;
      try {
        const res = await dispatch(fetchWhiteLabelCompany()).unwrap();
        if (res?.success) {
          const themeName = res.data?.themeName || "default";
          const selectedTheme =
            themeMap[themeName.toLowerCase()] || defaultTheme;
          setThemeState(selectedTheme);
          localStorage.setItem("themeName", themeName.toLowerCase());
        }
      } catch (error) {
        console.error("Failed to fetch white label data:", error);
      }
    };

    fetchTheme();
  }, [dispatch, token]);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
  }, [theme]);

  const changeTheme = (themeName) => {
    const selectedTheme = themeMap[themeName.toLowerCase()] || defaultTheme;
    setThemeState(selectedTheme);
    localStorage.setItem("themeName", themeName.toLowerCase());
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
